import { DataType } from '@shared/extension/enums/data-type.enum';
import { BehaviorSubject } from 'rxjs';
import { StoreName } from '../../enums';
import { LocalStorageItem } from '../../interfaces';
import { AbstractLocalStorageService } from '../../services/abstract/abstract-local-storage.service';
import { getLocalStorageKeyForStore } from '../../utils';
import { AbstractComponentStore } from './abstract-component.store';

export abstract class AbstractStore<T extends Object> extends AbstractComponentStore<T> {
  readonly initialized$ = new BehaviorSubject<boolean>(false);

  private dataTypesMap: Map<keyof T, DataType>;

  constructor(
    protected localStorageService: AbstractLocalStorageService,
    protected storeName: StoreName,
    private initialState: { [key in keyof T]: [T[key], DataType]}
  ) {
    const initialStateValues = {} as { [key in keyof T]: T[key] };
    const dataTypesMap = new Map<keyof T, DataType>();
    (Object.keys(initialState) as Array<keyof T>).forEach(
      (key: keyof T) => {
        initialStateValues[key] = initialState[key][0];
        dataTypesMap.set(key, initialState[key][1]);
      }
    );
    super(initialStateValues);

    this.dataTypesMap = dataTypesMap;
    this.restoreStateFromLocalStorage();
    this.handleLocalStorageChanges();
  }

  protected async setValue(key: keyof T, value: T[keyof T]): Promise<void> {
    this.updateStateProp(key, value);
    await this.setLocalStorage(key.toString(), value);
  }

  protected getKeyFromLocalStorageKey(localStorageKey: string): keyof T | null {
    if (!localStorageKey.startsWith(`${this.storeName}:`)) {
      return null;
    }
    localStorageKey = localStorageKey.replace(`${this.storeName}:`, '');
    return localStorageKey.charAt(0).toLowerCase() + localStorageKey.slice(1) as keyof T;
  }

  protected async getLocalStorage<D>(key: string, dataType: DataType, addStorePrefix: boolean = true): Promise<D | undefined> {
    const storageKey: string = getLocalStorageKeyForStore(key, this.storeName, addStorePrefix);
    return this.localStorageService.getLocalStorage<D>(storageKey, dataType);
  }

  protected async setLocalStorage(key: string, value: unknown, addStorePrefix: boolean = true): Promise<void> {
    const storageKey: string = getLocalStorageKeyForStore(key, this.storeName, addStorePrefix);
    return this.localStorageService.setLocalStorage(storageKey, value);
  }

  private restoreStateFromLocalStorage(): void {
    const promises: Promise<void>[] = (Object.keys(this.initialState) as Array<keyof T>).map((key: keyof T) => {
      return this.getLocalStorage(key.toString(), this.dataTypesMap.get(key)!).then((value: unknown) => {
        if (value !== undefined) {
          this.updateStateProp(key, value as T[keyof T]);
        }
      }).catch((ex) => {
        console.error(`Error while restoring ${key.toString()} from local storage`, ex);
      });
    });
    Promise.all(promises).then(() => {
      this.initialized$.next(true);
    });
  }

  private handleLocalStorageChanges(): void {
    this.localStorageService.handleLocalStorageChanges().subscribe((item: LocalStorageItem) => {
      const key: keyof T | null = this.getKeyFromLocalStorageKey(item.key);
      if (key && this.dataTypesMap.has(key)) {
        const dataType: DataType = this.dataTypesMap.get(key)!;
        const data = this.localStorageService.getDataFromType(item.value, dataType) as T[keyof T];
        if (this.state$.value[key] !== data || JSON.stringify(this.state$.value[key]) !== JSON.stringify(data)) {
          this.updateStateProp(key, data);
        }
      }
    });
  }
}
